import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import TypeAhead, { type TypeAheadOption } from '../../../components/TypeAhead';
import DateTimePicker from '../../../components/DateTimePicker';
import { parseDate, formatDateStr } from '../../../components/tablemaint/DateField';
import quoteService from '../../../services/quote.service';
import { formatWholeDollars } from '../../../utils/money';
import api from '../../../services/api';
import {
  rebillCreditService,
  type RebillCreditLineItem,
  type RebillCreditPayload,
} from '../../../services/rebillCredit.service';
import InvoiceOptionsDialog from '../../accounting/InvoiceOptionsDialog';

/**
 * Create/Edit modal for a single V1 Rebill/Credit record — the FastCMS-styled
 * port of V1's "New/Edit Rebill/Credit Info" popup. Edits the parent fields plus
 * the GL-code line items. recno === null means create; a number edits that record.
 *
 * Portals to <body> at z-[130] so it layers above the Pro slide-over (z-[100]);
 * an Escape handler runs in the capture phase and stops propagation so the key
 * closes this modal without also closing the drawer (mirrors RebillCreditListModal).
 */

// ── V1-parity dropdown constants (hardcoded — do NOT fetch these) ─────────────
const ADJUSTED_REBILL = ['', 'ADJUSTED', 'REBILL', 'CREDIT'];
const DEPT_RESPONSIBLE = ['', 'ACCOUNTING', 'COLLECTIONS EFFORT', 'CUSTOMER',
  'TRANSPORTATION OPERATIONS', 'SALES', 'SSL/TERMINAL WAIVED',
  'TRANSPORTATION CUSTOMER SERVICE', 'WIRE FEE', 'WHSE CUSTOMER SERVICE',
  'WHSE OPERATIONS', 'WHSE TRANSPORTATION', 'SYSTEM OUTAGE/IT'];
const YESNO = ['N', 'Y'];

// ── compact inline input classes (no font-mono — project rule) ────────────────
const INPUT_CLS =
  'min-w-0 flex-1 bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1.5 py-[3px] ' +
  'text-[0.75rem] text-slate-900 dark:text-gray-100 h-6 focus:outline-none focus:ring-1 focus:ring-blue-400';
const SELECT_CLS =
  'min-w-0 flex-1 bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1 py-[2px] ' +
  'text-[0.75rem] text-slate-900 dark:text-gray-100 h-6 cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-400';
const TEXTAREA_CLS =
  'min-w-0 flex-1 bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1.5 py-1 ' +
  'text-[0.75rem] text-slate-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-y';

interface GlOption {
  glCode: string;
  description: string;
}

/**
 * A `<select>` silently renders as blank when its value is not among its options, and
 * saving then writes that blank over the stored value. V1's `smod_forcevalueinarray`
 * exists for exactly this: it pushes the stored value into the list before drawing the
 * dropdown, so a legacy or since-retired value still displays and survives a save that
 * never touched it.
 *
 * Prepends `value` when it is non-blank and absent; returns `options` untouched
 * otherwise, so the common case allocates nothing new.
 */
export function withStoredValue(value: string, options: readonly string[]): readonly string[] {
  return value && !options.includes(value) ? [value, ...options] : options;
}

/**
 * Pure required-field validator — V1's save gate, rule for rule.
 *
 * `cms_chkempty` on Rebill Date, Credit Memo # and Sage Tag (lib_editrecord.pl:19563),
 * then `checkrebillli()` (cms.js:13134) on the lines:
 *
 *  * A record being DELETED skips the line checks entirely — you must be able to
 *    retire a bad record without first making its lines valid.
 *  * A line COUNTS once either half is filled in. A wholly blank row is ignored,
 *    not rejected: V1 always renders one empty row and the "Add" button adds more,
 *    so rejecting blanks would block a save the user never asked to change.
 *  * At least one counting line is REQUIRED. This rule was dropped in the original
 *    port; both downstream consumers filter on the line item (the Sage export's
 *    `AND amount > 0`, and the credit memo's `SUM(rli.amount)`), so a parent with
 *    no lines is a $0 credit memo that never exports and never errors. Prod has
 *    0 of 17,736 live records in that state — the rule has always held.
 *  * A counting line needs BOTH halves.
 *
 * Returns the first error message, or null when the payload is valid.
 */
export function validateRebillCredit(p: {
  rebillDate: string; creditMemoNum: string; sageTag: string; deleted?: string;
  lineItems: { glCode: string; amount: string | number; deleted?: string }[];
}): string | null {
  if (!p.rebillDate.trim()) return 'Rebill Date is required';
  if (!p.creditMemoNum.trim()) return 'Credit Memo # is required';
  if (!p.sageTag.trim()) return 'Sage Tag is required';
  if (p.deleted === 'Y') return null;
  let found = false;
  for (const li of p.lineItems) {
    if (li.deleted === 'Y') continue;
    // The backend returns amount as a JSON number; coerce before any string
    // method so the validator never throws on number|string|undefined.
    const gl = String(li.glCode ?? '').trim();
    const amt = String(li.amount ?? '').trim();
    if (!gl && amt === '') continue;
    found = true;
    if (!gl || amt === '') return 'Each line item requires both amount and GL code';
    if (Number.isNaN(Number(amt))) return 'Each line item needs a numeric Amount';
  }
  if (!found) return 'Need at least one line item to save rebill/credit';
  return null;
}

interface Props {
  open: boolean;
  pronumber: number;
  recno: number | null;   // null => create mode; number => edit that record
  onClose: () => void;
  onSaved: () => void;     // called after a successful create/update
}

/** Right-aligned bold label + field, on one row. */
const Row: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({
  label,
  required,
  children,
}) => (
  <div className="flex items-center gap-2 py-0.5">
    <label className="w-40 shrink-0 text-right text-[0.75rem] font-semibold text-slate-600 dark:text-gray-300">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    <div className="flex min-w-0 flex-1 items-center">{children}</div>
  </div>
);

const RebillCreditEditModal: React.FC<Props> = ({ open, pronumber, recno, onClose, onSaved }) => {
  // Parent fields (all strings).
  const [adjustedRebill, setAdjustedRebill] = useState('');
  const [rebillDate, setRebillDate] = useState('');
  const [creditMemoNum, setCreditMemoNum] = useState('');
  const [rebilledInvoice, setRebilledInvoice] = useState('');
  const [applyTo, setApplyTo] = useState('');
  const [sageTag, setSageTag] = useState('');            // stored mas90code
  const [sageTagDisplay, setSageTagDisplay] = useState(''); // text shown in the typeahead
  const [deptResponsible, setDeptResponsible] = useState('');
  const [preventable, setPreventable] = useState('N');
  const [comments, setComments] = useState('');
  const [deleted, setDeleted] = useState('N');
  const [createdBy, setCreatedBy] = useState('');
  const [createdAtDisplay, setCreatedAtDisplay] = useState('');

  const [applyToOptions, setApplyToOptions] = useState<string[]>([]);
  const [lineItems, setLineItems] = useState<RebillCreditLineItem[]>([]);
  const [glOptions, setGlOptions] = useState<GlOption[]>([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sage Tag typeahead (debounced billto lookup).
  const [sageOptions, setSageOptions] = useState<TypeAheadOption[]>([]);
  const [sageSearching, setSageSearching] = useState(false);
  const sageTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sageSeq = useRef(0);

  const isEdit = recno != null;

  // Credit Memo Options — V1 puts this link at the top of the modal, and only on a
  // record that exists (lib_editrecord.pl:19326: `if ($rdata->{'recno'})`). A record
  // being created has no credit memo to view, print or send yet.
  const [memoOptionsOpen, setMemoOptionsOpen] = useState(false);
  const [memoNote, setMemoNote] = useState('');

  // Capture-phase Escape: close THIS modal and stop the event before it reaches
  // the slide-over's own Escape handler (which would close the drawer).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      // The Credit Memo Options dialog opens on top of this one and has no Escape
      // handler of its own. Without this, Escape reached here first and closed the
      // modal UNDERNEATH the dialog the user was looking at.
      if (memoOptionsOpen) {
        setMemoOptionsOpen(false);
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose, memoOptionsOpen]);

  // Always fetch the GL-code options while open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .get('/cms-lookups/credit-glcodes')
      .then((r) => {
        if (!cancelled) setGlOptions(r.data as GlOption[]);
      })
      .catch(() => {
        if (!cancelled) setGlOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Load parent + line items on open (create resets to blanks; edit hydrates).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setSageOptions([]);

    if (!isEdit) {
      // CREATE — reset to blanks and pull applyToOptions from the list endpoint.
      setAdjustedRebill('');
      setRebillDate('');
      setCreditMemoNum('');
      setRebilledInvoice('');
      setApplyTo('');
      setSageTag('');
      setSageTagDisplay('');
      setDeptResponsible('');
      setPreventable('N');
      setComments('');
      setDeleted('N');
      setCreatedBy('');
      setCreatedAtDisplay('');
      setLineItems([]);
      setLoading(false);
      rebillCreditService
        .list(pronumber)
        .then((res) => {
          if (!cancelled) setApplyToOptions(res.applyToOptions);
        })
        .catch(() => {
          if (!cancelled) setApplyToOptions([]);
        });
      return () => {
        cancelled = true;
      };
    }

    // EDIT — hydrate all fields from the record.
    setLoading(true);
    rebillCreditService
      .get(pronumber, recno)
      .then((rec) => {
        if (cancelled) return;
        setAdjustedRebill(rec.adjustedRebill);
        setRebillDate(rec.rebillDate);
        setCreditMemoNum(rec.creditMemoNum);
        setRebilledInvoice(rec.rebilledInvoice);
        setApplyTo(rec.applyTo);
        setSageTag(rec.sageTag);
        setSageTagDisplay(rec.sageTagName || rec.sageTag);
        setDeptResponsible(rec.deptResponsible);
        setPreventable(rec.preventable || 'N');
        setComments(rec.comments);
        setDeleted(rec.deleted || 'N');
        setCreatedBy(rec.createdBy);
        setCreatedAtDisplay(rec.createdAtDisplay);
        setApplyToOptions(rec.applyToOptions);
        // The backend serializes amount as a JSON number; coerce each line's
        // fields to strings so state matches the declared `string` type and the
        // Amount <input> (a number here would crash .trim() in the validator).
        setLineItems(
          (rec.lineItems ?? []).map((li) => ({
            ...li,
            amount: String(li.amount ?? ''),
            glCode: String(li.glCode ?? ''),
            deleted: li.deleted ?? 'N',
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load rebill/credit record.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, pronumber, recno, isEdit]);

  // Debounced bill-to lookup for the Sage Tag typeahead (min 2 chars, ~300ms) —
  // modeled on BillToLookupField.
  const searchSage = (term: string) => {
    if (sageTimer.current) clearTimeout(sageTimer.current);
    const q = term.trim();
    if (q.length < 2) {
      setSageOptions([]);
      setSageSearching(false);
      return;
    }
    // In flight from the FIRST keystroke (debounce included) so the dropdown shows
    // "Searching…" instead of a premature "No results found"; the sequence counter
    // drops out-of-order responses from stale keystrokes (LookupSearchField pattern).
    const seq = ++sageSeq.current;
    setSageSearching(true);
    sageTimer.current = setTimeout(async () => {
      try {
        const rows = await quoteService.lookupBillTo(q);
        if (seq !== sageSeq.current) return;
        setSageOptions(
          rows.map((r) => ({
            value: String(r.billto),
            label: r.billtoname,
            labelSuffix: `#${r.billto}`,
            description: [
              [r.city, r.state].filter(Boolean).join(', '),
              // Sage tag with its rounded AR balance, e.g. "MAERSK2 - $340,592".
              [r.mas90code, r.arBalance !== '' ? formatWholeDollars(r.arBalance) : '']
                .filter(Boolean).join(' - '),
            ].filter(Boolean).join('  ·  '),
            data: r,
          })),
        );
      } catch {
        if (seq === sageSeq.current) setSageOptions([]);
      } finally {
        if (seq === sageSeq.current) setSageSearching(false);
      }
    }, 300);
  };

  const addLineItem = () => {
    setLineItems((prev) => [...prev, { glCode: '', amount: '', deleted: 'N' }]);
  };

  const updateLineItem = (index: number, patch: Partial<RebillCreditLineItem>) => {
    setLineItems((prev) => prev.map((li, i) => (i === index ? { ...li, ...patch } : li)));
  };

  const deleteLineItem = (index: number) => {
    setLineItems((prev) => {
      const li = prev[index];
      // Persisted line → soft-delete (keep it in state so it's sent as deleted).
      if (li.recno) return prev.map((x, i) => (i === index ? { ...x, deleted: 'Y' } : x));
      // Brand-new line → drop it from the array.
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSave = async () => {
    const msg = validateRebillCredit({
      rebillDate, creditMemoNum, sageTag, deleted, lineItems,
    });
    if (msg) {
      setError(msg);
      return;
    }
    const payload: RebillCreditPayload = {
      adjustedRebill,
      rebillDate,
      creditMemoNum,
      rebilledInvoice,
      applyTo,
      sageTag,
      deptResponsible,
      preventable,
      comments,
      deleted,
      lineItems,
    };
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await rebillCreditService.update(pronumber, recno, payload);
      } else {
        await rebillCreditService.create(pronumber, payload);
      }
      onSaved();
      onClose();
    } catch {
      setError('Failed to save rebill/credit record.');
    } finally {
      setSaving(false);
    }
  };

  // Alt+S saves, Alt+C closes — mirroring the footer buttons and their `saving`
  // guard. The layer keeps both off the Pro drawer behind this modal.
  // Alt+S / Alt+C are this modal's; they go quiet while the Credit Memo Options dialog
  // is on top of it, so Alt+C cannot close the modal underneath an open dialog.
  const hotkeysLive = open && !memoOptionsOpen;
  useAltHotkeys(
    { S: saving ? null : handleSave, C: saving ? null : onClose },
    undefined,
    { layer: hotkeysLive },
  );

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  // Every dropdown keeps whatever is stored, even when it is no longer offered:
  //  * Dept Responsible — V1 dropped six values (INC-28012) that 3,900+ live records
  //    still carry, `CUSTOMER AGREEMENT W/ PROVIDER` alone accounting for 3,148.
  //  * Apply To — the options are the masterpros of THIS pro's live invoices, so
  //    cancelling an invoice after the credit was raised would drop its own value.
  //  * Adjusted/Rebilled — a fixed list, but three live records store a blank that
  //    is in the list and nothing guarantees the list never changes again.
  const deptOptions = withStoredValue(deptResponsible, DEPT_RESPONSIBLE);
  const applyToChoices = withStoredValue(applyTo, applyToOptions);
  const adjustedRebillChoices = withStoredValue(adjustedRebill, ADJUSTED_REBILL);

  // "<code> — <description>" for a code in the picklist; the bare code for one that
  // has left it (we have no description to show, and inventing one would be worse).
  const glLabel = (code: string): string => {
    const match = glOptions.find((o) => o.glCode === code);
    return match ? `${match.glCode} — ${match.description}` : code;
  };

  const visibleLineItems = lineItems
    .map((li, index) => ({ li, index }))
    .filter(({ li }) => li.deleted !== 'Y');

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Edit Rebill/Credit Info' : 'New Rebill/Credit Info'}
        onClick={onClose}
        data-testid="rebill-credit-edit-modal"
      >
      <div
        ref={panelRef}
        style={panelStyle}
        className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-panel shadow-xl dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={isEdit ? 'Edit Rebill/Credit Info' : 'New Rebill/Credit Info'}
          onClose={onClose}
          closeLabel="Close rebill/credit editor"
          dragHandleProps={dragHandleProps}
        />

        {/* Body */}
        <div className="overflow-y-auto px-4 py-3 text-[0.78125rem] text-slate-700 dark:text-gray-200">
          {loading ? (
            <p className="py-6 text-center text-slate-400 dark:text-gray-500">Loading…</p>
          ) : (
            <>
              {isEdit && (
                <div className="mb-1.5 flex items-center gap-2 border-b border-slate-200 pb-1.5 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => { setMemoNote(''); setMemoOptionsOpen(true); }}
                    className="text-[0.75rem] font-semibold text-blue-600 hover:underline dark:text-blue-400"
                  >
                    Credit Memo Options
                  </button>
                  {memoNote && (
                    <span className="text-[0.71875rem] text-emerald-700 dark:text-emerald-400">
                      {memoNote}
                    </span>
                  )}
                </div>
              )}
              <Row label="Created by">
                <span className="text-[0.75rem] text-slate-500 dark:text-gray-400">
                  {createdBy || '—'}
                </span>
              </Row>
              <Row label="Date/Time">
                <span className="text-[0.75rem] text-slate-500 dark:text-gray-400">
                  {createdAtDisplay || '—'}
                </span>
              </Row>
              <Row label="Adjusted/Rebilled">
                <select
                  className={SELECT_CLS}
                  aria-label="Adjusted/Rebilled"
                  value={adjustedRebill}
                  onChange={(e) => setAdjustedRebill(e.target.value)}
                >
                  {adjustedRebillChoices.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </Row>
              <Row label="Rebill Date" required>
                <DateTimePicker
                  mode="date"
                  selectionType="single"
                  inputVariant="modal"
                  labelType="none"
                  ariaLabel="Rebill Date"
                  autoApply
                  usePortal
                  placeholder="MM/DD/YYYY"
                  value={parseDate(rebillDate)}
                  onChange={(val) => setRebillDate(formatDateStr(val instanceof Date ? val : null))}
                  className="w-full"
                />
              </Row>
              <Row label="Credit Memo #" required>
                <input
                  type="text"
                  className={INPUT_CLS}
                  aria-label="Credit Memo #"
                  value={creditMemoNum}
                  onChange={(e) => setCreditMemoNum(e.target.value.toUpperCase())}
                />
              </Row>
              <Row label="Rebilled Invoice #">
                <input
                  type="text"
                  className={INPUT_CLS}
                  aria-label="Rebilled Invoice #"
                  value={rebilledInvoice}
                  onChange={(e) => setRebilledInvoice(e.target.value.toUpperCase())}
                />
              </Row>
              <Row label="Apply To">
                <select
                  className={SELECT_CLS}
                  aria-label="Apply To"
                  value={applyTo}
                  onChange={(e) => setApplyTo(e.target.value)}
                >
                  {applyToChoices.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </Row>
              <Row label="Sage Tag" required>
                <TypeAhead
                  freeText
                  compact
                  multiple={false}
                  showDescriptions
                  value={sageTagDisplay}
                  options={sageOptions}
                  onChange={(v) => {
                    // Typing clears the resolved code, forcing an explicit pick.
                    setSageTagDisplay(v);
                    setSageTag('');
                  }}
                  onSearch={searchSage}
                  onSelect={(opt) => {
                    const r = opt.data as { mas90code?: string; billtoname?: string };
                    setSageTag(r.mas90code || '');
                    setSageTagDisplay(r.billtoname || r.mas90code || '');
                  }}
                  ariaLabel="Sage Tag"
                  placeholder="Search by name, Sage Tag, or #…"
                  className="w-full"
                  searching={sageSearching}
                  emptyMessage={sageTagDisplay.trim().length < 2
                    ? 'Type at least 2 characters to search'
                    : undefined}
                />
              </Row>
              <Row label="Dept Responsible">
                <select
                  className={SELECT_CLS}
                  aria-label="Dept Responsible"
                  value={deptResponsible}
                  onChange={(e) => setDeptResponsible(e.target.value)}
                >
                  {deptOptions.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </Row>
              <Row label="Preventable">
                <select
                  className={SELECT_CLS}
                  aria-label="Preventable"
                  value={preventable}
                  onChange={(e) => setPreventable(e.target.value)}
                >
                  {YESNO.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </Row>
              <Row label="Comments">
                <textarea
                  className={TEXTAREA_CLS}
                  aria-label="Comments"
                  rows={3}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                />
              </Row>
              <Row label="Deleted">
                <select
                  className={SELECT_CLS}
                  aria-label="Deleted"
                  value={deleted}
                  onChange={(e) => setDeleted(e.target.value)}
                >
                  {YESNO.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </Row>

              {/* Line items */}
              <div className="mt-3 border-t border-slate-200 pt-2 dark:border-gray-700">
                <div className="mb-1 flex items-center justify-between">
                  <h3 className="text-[0.75rem] font-bold text-slate-700 dark:text-gray-200">
                    Add/Edit Rebill/Credit Line Items
                  </h3>
                  <button
                    type="button"
                    onClick={addLineItem}
                    aria-label="Add line item"
                    className="inline-flex items-center gap-1 rounded border border-slate-300 px-1.5 py-0.5 text-[0.6875rem] font-semibold text-slate-700 hover:bg-slate-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <PlusIcon className="h-3.5 w-3.5" /> Add
                  </button>
                </div>
                {visibleLineItems.length === 0 ? (
                  <p className="py-2 text-center text-[0.71875rem] text-slate-400 dark:text-gray-500">
                    No line items
                  </p>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                        <th className="py-1">GL Code</th>
                        <th className="py-1 w-28">Amount</th>
                        <th className="py-1 w-16 text-center">Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleLineItems.map(({ li, index }) => (
                        <tr
                          key={li.recno ?? `new-${index}`}
                          className="border-t border-slate-100 dark:border-gray-700/60"
                        >
                          <td className="py-1 pr-2">
                            <select
                              className={SELECT_CLS}
                              aria-label="GL Code"
                              value={li.glCode}
                              onChange={(e) => updateLineItem(index, { glCode: e.target.value })}
                            >
                              <option value="" />
                              {/* Same reason as the dropdowns above: a GL code that has
                                  since lost `creditgl='Y'` (or been deleted) is absent
                                  from the picklist, and without this the row would blank
                                  itself on open and save that blank. */}
                              {withStoredValue(
                                li.glCode,
                                glOptions.map((o) => o.glCode),
                              ).map((code) => (
                                <option key={code} value={code}>
                                  {glLabel(code)}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="py-1 pr-2">
                            <input
                              type="number"
                              className={INPUT_CLS}
                              aria-label="Amount"
                              value={li.amount}
                              onChange={(e) => updateLineItem(index, { amount: e.target.value })}
                            />
                          </td>
                          <td className="py-1 text-center">
                            <button
                              type="button"
                              onClick={() => deleteLineItem(index)}
                              aria-label="Delete line item"
                              className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:text-gray-500 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {error && (
                <p className="mt-2 text-[0.75rem] font-semibold text-red-600 dark:text-red-400">
                  {error}
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-2.5 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 px-3 py-1.5 text-[0.75rem] font-semibold text-slate-700 hover:bg-slate-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded bg-blue-600 px-3 py-1.5 text-[0.75rem] font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      </div>

      {/* Credit Memo Options — the shared Invoice Options dialog in its `creditmemo`
          mode, which creates nothing (so `onConfirm` has nothing to do).

          A SIBLING of this modal's overlay, not a child of its panel: the panel carries
          a drag `transform` and `overflow-hidden`, and a `position: fixed` descendant of
          a transformed element is positioned and CLIPPED by it. As a sibling it also
          stops its own backdrop click from bubbling into this modal's `onClose`. It
          needs an explicit layer above this modal's z-[130]. */}
      {memoOptionsOpen && recno != null && (
        <InvoiceOptionsDialog
          mode={{ kind: 'creditmemo', pronumber, recno }}
          billto={0}
          title="Credit Memo Options"
          layerClassName="z-[140]"
          onConfirm={async () => []}
          onClose={() => setMemoOptionsOpen(false)}
          onDone={({ note }) => {
            setMemoOptionsOpen(false);
            setMemoNote(note || 'Done.');
          }}
        />
      )}
    </>,
    document.body,
  );
};

export default RebillCreditEditModal;
